import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import api from 'app/api';
import { RootState } from 'types';
import { useAuthPageSlice } from 'app/pages/Auth/slice';
import { Business } from 'app/pages/Auth/slice/types';
import { decodeChatParam, CHAT_URL_PARAM } from 'utils/chatUrlEncoder';
import {
  getTabId,
  resetTabId,
  getTabToken,
  setTabToken,
  getIdentityToken,
  businessIdFromToken,
  adoptTabTokenAsIdentity,
} from 'utils/tabAuth';
import {
  readOpenTabs,
  claimBusiness,
  releaseTab,
  subscribe,
  postMessage,
  pickPreferredBusiness,
  HEARTBEAT_MS,
} from 'utils/tabBusinessRegistry';

export type TabBusinessStatus = 'booting' | 'active';

const DUPLICATE_PING_WAIT_MS = 200;
const BOOT_SAFETY_MS = 5000;

// Stable reference so the useSelector default never triggers a re-render storm.
const NO_BUSINESSES: Business[] = [];

/** Mint this tab's token for `businessId` (adopt identity for the default). */
const tokenForBusiness = async (businessId: number): Promise<string | null> => {
  if (businessId === businessIdFromToken(getIdentityToken())) {
    return getIdentityToken();
  }
  // Parallel so a sibling tab's session is never revoked by this one.
  const res = await api.get(`/org/business-token/${businessId}?parallel=1`);
  return res?.data?.data?.token ?? null;
};

/**
 * Bind `businessId` to this tab: get its token, store it, and record the claim
 * in the registry. Returns false if no token could be obtained. Claiming is
 * non-exclusive — other tabs may hold the same business at the same time.
 */
const pinBusinessToTab = async (businessId: number): Promise<boolean> => {
  const token = await tokenForBusiness(businessId);
  if (!token) return false;
  setTabToken(token);
  claimBusiness(businessId);
  return true;
};

/** The business id embedded in a `?chat=` deep link in the current URL, if any. */
const deepLinkBusinessId = (): number | null => {
  try {
    const param = new URLSearchParams(window.location.search).get(
      CHAT_URL_PARAM,
    );
    return param ? (decodeChatParam(param).businessId ?? null) : null;
  } catch {
    return null;
  }
};

/** Listen briefly for a live tab answering to my id (duplicated-tab signal). */
const detectDuplicate = (): Promise<boolean> =>
  new Promise(resolve => {
    const myId = getTabId();
    let found = false;
    const unsub = subscribe(msg => {
      if (msg.type === 'pong' && msg.tabId === myId) found = true;
    });
    postMessage({ type: 'ping', tabId: myId });
    setTimeout(() => {
      unsub();
      resolve(found);
    }, DUPLICATE_PING_WAIT_MS);
  });

/**
 * Owns this tab's business identity: which business this tab runs. A refreshed
 * or duplicated tab keeps the business it already holds; a fresh tab opens the
 * shared identity business — which tracks whichever tab the user was last
 * looking at (see `adoptTabTokenAsIdentity`) and, on re-login, the business the
 * backend says they were last active in. The same business may be open in any
 * number of tabs at once (non-exclusive: no takeover, no "use here" see-saw).
 */
export const useTabBusiness = (enabled: boolean) => {
  const dispatch = useDispatch();
  const { actions } = useAuthPageSlice();
  const orgBusinesses = useSelector(
    (state: RootState) => state.authPage?.orgBusinesses ?? NO_BUSINESSES,
  );

  const [status, setStatus] = useState<TabBusinessStatus>('booting');
  const activeBusinessIdRef = useRef<number | null>(null);
  const bootstrappedRef = useRef(false);

  const setActive = useCallback((businessId: number) => {
    activeBusinessIdRef.current = businessId;
    // While this tab is the one on screen, promote its token to the shared
    // identity so a fresh tab — which falls back to the identity token — opens
    // the same business. A hidden tab that (re)bootstraps must NOT clobber the
    // identity the visible tab set, so guard on visibility.
    if (document.visibilityState === 'visible') adoptTabTokenAsIdentity();
    setStatus('active');
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      // Duplicated tab (Chrome copies sessionStorage) → take a fresh id so it is
      // treated as a new tab. Only possible when my id is already registered, so
      // skip the ping wait for a genuinely new tab.
      if (readOpenTabs()[getTabId()] && (await detectDuplicate())) {
        resetTabId();
      }

      const accessibleIds = orgBusinesses
        .map(b => b.id)
        .filter((id): id is number => typeof id === 'number');
      const identityBizId = businessIdFromToken(getIdentityToken());

      // Refresh / session-restore / duplicated tab (Chrome copies the tab
      // token): this tab already owns a business — keep it, even if other tabs
      // hold the same one (tabs are no longer exclusive).
      const ownedBizId = businessIdFromToken(getTabToken());
      if (ownedBizId != null) {
        claimBusiness(ownedBizId);
        setActive(ownedBizId);
        return;
      }

      // A `?chat=` deep link targets a specific business. Otherwise a fresh tab
      // (or a fresh login) opens the identity business — which tracks whichever
      // tab the user was last looking at, and on re-login is set by the backend
      // to the business they were last active in — else the lowest accessible
      // id. It never auto-switches; the user can switch from there.
      const linkBizId = deepLinkBusinessId();
      const pick =
        linkBizId != null && accessibleIds.includes(linkBizId)
          ? linkBizId
          : pickPreferredBusiness(accessibleIds, identityBizId);
      if (pick == null) {
        setStatus('active');
        return;
      }
      if (!(await pinBusinessToTab(pick))) {
        setStatus('active');
        return;
      }
      // The default business needs no reload — the app already booted into it;
      // a different business reloads so the app re-boots with the tab token.
      if (pick === identityBizId) setActive(pick);
      else window.location.reload();
    } catch {
      // Never block the app on a coordination failure.
      setStatus('active');
    }
  }, [orgBusinesses, setActive]);

  // Pull the accessible-business list (used to resolve / fall back the pick).
  useEffect(() => {
    if (enabled && orgBusinesses.length === 0) {
      dispatch(actions.getOrgBusinessStart());
    }
  }, [enabled, orgBusinesses.length, dispatch, actions]);

  // Bootstrap once the accessible businesses are known.
  useEffect(() => {
    if (!enabled || bootstrappedRef.current || orgBusinesses.length === 0) {
      return;
    }
    bootstrappedRef.current = true;
    void bootstrap();
  }, [enabled, orgBusinesses.length, bootstrap]);

  // Safety net: never leave the app stuck on the boot loader.
  useEffect(() => {
    if (!enabled) return;
    const timer = setTimeout(
      () => setStatus(s => (s === 'booting' ? 'active' : s)),
      BOOT_SAFETY_MS,
    );
    return () => clearTimeout(timer);
  }, [enabled]);

  // Answer duplicate-detection pings so a Chrome-duplicated tab (which inherits
  // this tab's id) can tell its id is already live and mint a fresh one.
  useEffect(() => {
    if (!enabled) return;
    return subscribe(msg => {
      if (msg.type === 'ping' && msg.tabId === getTabId()) {
        postMessage({ type: 'pong', tabId: getTabId() });
      }
    });
  }, [enabled]);

  // Heartbeat the claim while active; release the slot when the tab closes.
  useEffect(() => {
    if (status !== 'active' || activeBusinessIdRef.current == null) return;
    const bizId = activeBusinessIdRef.current;
    const heartbeat = setInterval(() => claimBusiness(bizId), HEARTBEAT_MS);
    const onUnload = () => releaseTab();
    window.addEventListener('beforeunload', onUnload);
    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [status]);

  // Keep the shared identity pointed at whichever tab the user is actually
  // looking at: when this tab becomes visible/focused, promote its token to the
  // identity. So a new tab / next login reopens the business they were last
  // *working in* — not merely the last one they switched into. Mirrors the
  // backend's lastUsedAt ordering.
  useEffect(() => {
    if (status !== 'active') return;
    const sync = () => {
      if (document.visibilityState === 'visible') adoptTabTokenAsIdentity();
    };
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
    };
  }, [status]);

  return { status };
};
